import type { DataTableColumn } from './types';
import { log } from '../utils/DebugLogger';

/**
 * Dependencies injected by the calling component so helpers can use
 * composable-scoped `n`, `formatDate`, and `formatDateTime`.
 */
export interface CellRenderDeps {
  n: (value: number, format?: string) => string;
  formatDate: (date: Date) => string;
  formatDateTime: (date: Date) => string;
}

/**
 * Format a cell value for display based on its data type.
 * Used by both DataTable.vue (master cells) and DataTableCell.vue (sub-cells).
 */
export function getDisplayValue(
  rowData: Record<string, unknown>,
  field: string,
  deps: CellRenderDeps,
  type: string | null = null,
): unknown {
  const value = rowData[field];
  if (value === null || value === undefined || value === '') return '';

  if (type === 'datetime' || type === 'date') {
    try {
      let dateObj = value;
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
        dateObj = new Date(value);
      }
      if (dateObj instanceof Date && !isNaN(dateObj.getTime())) {
        return type === 'datetime' ? deps.formatDateTime(dateObj) : deps.formatDate(dateObj);
      }
    } catch (e) {
      log('cellRenderers', 'getDisplayValue', 'Error formatting date', e);
    }
  }

  if (value instanceof Date) {
    try {
      if (value.getHours() !== 0 || value.getMinutes() !== 0 || value.getSeconds() !== 0) {
        return deps.formatDateTime(value);
      }
      return deps.formatDate(value);
    } catch (e) {
      log('cellRenderers', 'getDisplayValue', 'Error formatting date', e);
      return value.toString();
    }
  }

  return value;
}

/**
 * Render a merged sub-column value as a plain string.
 * Supports display-only renderTypes (text, currency, number, etc.).
 * Returns ' ' for null/empty values so row height stays uniform.
 */
export function renderSubValue(
  subCol: DataTableColumn,
  rowData: Record<string, unknown>,
  deps: CellRenderDeps,
): string {
  if (!subCol.data) return ' ';
  const v = rowData[subCol.data];
  if (v === null || v === undefined || v === '') return ' ';

  const rt = subCol.renderType;
  if (rt === 'currency') return deps.n(parseFloat(String(v)) || 0, 'currency');
  if (rt === 'number') return deps.n(parseFloat(String(v)) || 0, 'decimal');
  if (rt === 'integer') return deps.n(parseFloat(String(v)) || 0, 'integer');
  if (rt === 'percentage') return deps.n((parseFloat(String(v)) || 0) / 100, 'percent');
  if (rt === 'date') return String(getDisplayValue(rowData, subCol.data, deps, 'date'));
  if (rt === 'datetime') return String(getDisplayValue(rowData, subCol.data, deps, 'datetime'));
  if (rt === 'yes_no') return v ? 'Sim' : 'Não';
  if (rt === 'active_inactive') return v ? 'Ativo' : 'Inativo';
  return String(v);
}

/**
 * Generate a data-testid for a cell based on column, row index, and prefix.
 */
export function getCellTestId(
  column: DataTableColumn,
  rowIndex: number,
  testIdPrefix: string,
  suffix?: string,
): string {
  const colKey = (column && column.data) || 'col';
  return `${testIdPrefix}-row-${rowIndex}-${colKey}${suffix ? `-${suffix}` : ''}`;
}

/**
 * Resolve the data-testid for an action_buttons button.
 */
export function resolveButtonTestId(
  btn: Record<string, unknown>,
  column: DataTableColumn,
  rowIndex: number,
  testIdPrefix: string,
): string {
  const tid = btn.testId;
  if (typeof tid === 'function') {
    return String(tid({}));
  }
  return String(tid || getCellTestId(column, rowIndex, testIdPrefix, `action-${rowIndex}`));
}

/**
 * Resolve image sources for an image-renderType column.
 * Returns a normalized array of non-empty URL strings.
 */
export function resolveImageSources(
  column: DataTableColumn,
  row: Record<string, unknown>,
): string[] {
  const cfg = column.imageConfig;
  let raw: unknown;

  if (cfg?.src) {
    raw = cfg.src(row);
  } else if (column.data) {
    raw = row[column.data];
  } else {
    return [];
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(raw)) {
    return raw.filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
             .map(s => s.trim());
  }
  return [];
}

/**
 * Resolve the alt text for an image cell.
 */
export function resolveImageAlt(
  column: DataTableColumn,
  row: Record<string, unknown>,
): string {
  const cfg = column.imageConfig;
  if (typeof cfg?.alt === 'function') return cfg.alt(row);
  if (typeof cfg?.alt === 'string') return cfg.alt;
  return column.title ?? '';
}
